import React, { useState, useEffect } from 'react'
import { Plus, Upload, Video, Music, FileText, Image, Search, Filter, Eye, Download, Trash2, Edit, Play } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { mediaService, MediaItem, MediaCategory } from '@/services/mediaService'
import { AdminUser } from '@/lib/authService'
import { BibleVerseLink } from '@/components/BibleVerseLink'
import { useSubscription } from '@/hooks/useSubscription'
import { FeatureRestricted } from '@/components/subscription/FeatureRestricted'

interface MediaLibraryProps {
  user: AdminUser | null
}

export default function MediaLibrary({ user }: MediaLibraryProps) {
  const { features, limits, loading: subscriptionLoading } = useSubscription(user?.churchId)
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([])
  const [categories, setCategories] = useState<MediaCategory[]>([])
  const [loading, setLoading] = useState(true)
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false)
  const [editingItem, setEditingItem] = useState<MediaItem | null>(null)

  // Filters
  const [searchQuery, setSearchQuery] = useState('')
  const [filterCategory, setFilterCategory] = useState<string>('all')
  const [filterType, setFilterType] = useState<string>('all')
  const [filterStatus, setFilterStatus] = useState<string>('published')

  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    media_type: 'video' as MediaItem['media_type'],
    category_id: '',
    speaker: '',
    sermon_date: '',
    sermon_text: '',
    tags: '',
    is_public: true,
    is_featured: false,
    status: 'published' as MediaItem['status']
  })
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [selectedThumbnail, setSelectedThumbnail] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)

  // Stats
  const [stats, setStats] = useState({
    total_items: 0,
    total_views: 0,
    by_type: {} as Record<string, number>,
    by_category: {} as Record<string, number>
  })

  useEffect(() => {
    if (user?.churchId) {
      loadData()
    }
  }, [user?.churchId, filterCategory, filterType, filterStatus, searchQuery])

  const loadData = async () => {
    if (!user?.churchId) return

    try {
      setLoading(true)
      const [itemsData, categoriesData, statsData] = await Promise.all([
        mediaService.getMediaItems(user.churchId, {
          category_id: filterCategory !== 'all' ? filterCategory : undefined,
          media_type: filterType !== 'all' ? filterType : undefined,
          status: filterStatus !== 'all' ? filterStatus : undefined,
          search: searchQuery || undefined
        }),
        mediaService.getCategories(user.churchId),
        mediaService.getMediaStats(user.churchId)
      ])

      setMediaItems(itemsData)
      setCategories(categoriesData)
      setStats(statsData)
    } catch (error) {
      console.error('Error loading media:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedFile(file)
      // Auto-fill title from filename if empty
      if (!uploadForm.title) {
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '')
        setUploadForm(prev => ({ ...prev, title: nameWithoutExt }))
      }
    }
  }

  const handleThumbnailSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedThumbnail(file)
    }
  }

  const handleUpload = async () => {
    if (!selectedFile || !user?.churchId) return

    try {
      setUploading(true)
      setUploadProgress(0)

      // Upload main file
      const { url: fileUrl } = await mediaService.uploadFile(
        selectedFile,
        user.churchId,
        (progress) => {
          setUploadProgress(Math.round((progress.loaded / progress.total) * 100))
        }
      )

      // Upload thumbnail if provided
      let thumbnailUrl: string | undefined
      if (selectedThumbnail) {
        const { url } = await mediaService.uploadThumbnail(selectedThumbnail, user.churchId)
        thumbnailUrl = url
      }

      // Create media item
      const tags = uploadForm.tags ? uploadForm.tags.split(',').map(t => t.trim()) : []

      await mediaService.createMediaItem({
        church_id: user.churchId,
        title: uploadForm.title,
        description: uploadForm.description || undefined,
        media_type: uploadForm.media_type,
        category_id: uploadForm.category_id || undefined,
        file_url: fileUrl,
        file_name: selectedFile.name,
        file_size: selectedFile.size,
        mime_type: selectedFile.type,
        speaker: uploadForm.speaker || undefined,
        sermon_date: uploadForm.sermon_date || undefined,
        sermon_text: uploadForm.sermon_text || undefined,
        tags,
        thumbnail_url: thumbnailUrl,
        is_public: uploadForm.is_public,
        is_featured: uploadForm.is_featured,
        status: uploadForm.status,
        published_at: uploadForm.status === 'published' ? new Date().toISOString() : undefined,
        created_by: user.id
      })

      // Reset form
      setUploadForm({
        title: '',
        description: '',
        media_type: 'video',
        category_id: '',
        speaker: '',
        sermon_date: '',
        sermon_text: '',
        tags: '',
        is_public: true,
        is_featured: false,
        status: 'published'
      })
      setSelectedFile(null)
      setSelectedThumbnail(null)
      setUploadDialogOpen(false)

      // Reload data
      await loadData()
    } catch (error) {
      console.error('Upload error:', error)
      alert('Failed to upload media. Please try again.')
    } finally {
      setUploading(false)
      setUploadProgress(0)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this media item?')) return

    try {
      await mediaService.deleteMediaItem(id)
      await loadData()
    } catch (error) {
      console.error('Delete error:', error)
      alert('Failed to delete media item.')
    }
  }

  const getMediaTypeIcon = (type: string) => {
    switch (type) {
      case 'video': return <Video className="h-4 w-4" />
      case 'audio': return <Music className="h-4 w-4" />
      case 'document':
      case 'pdf': return <FileText className="h-4 w-4" />
      case 'image': return <Image className="h-4 w-4" />
      default: return <FileText className="h-4 w-4" />
    }
  }

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown'
    const mb = bytes / (1024 * 1024)
    return `${mb.toFixed(2)} MB`
  }

  const formatDuration = (seconds?: number) => {
    if (!seconds) return ''
    const minutes = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${minutes}:${secs.toString().padStart(2, '0')}`
  }

  if (loading || subscriptionLoading) {
    return <div className="text-center py-12">Loading...</div>
  }

  if (!features.mediaLibrary) {
    return (
      <FeatureRestricted
        featureName="Media Library"
        description="Store and manage sermon recordings, worship songs, teaching videos, and church documents in one organized library."
        currentPlan={limits?.plan || 'starter'}
        requiredPlan="growth"
        icon={<Video className="h-10 w-10 text-white" />}
      />
    )
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Media Library</h1>
          <p className="text-muted-foreground">
            Manage sermons, worship recordings, and other media content
          </p>
        </div>
        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Upload className="mr-2 h-4 w-4" />
              Upload Media
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Upload Media</DialogTitle>
              <DialogDescription>
                Upload a new sermon, audio, video, or document to your media library
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              {/* File Upload */}
              <div className="grid gap-2">
                <Label htmlFor="file">Media File *</Label>
                <Input
                  id="file"
                  type="file"
                  onChange={handleFileSelect}
                  accept="video/*,audio/*,application/pdf,.doc,.docx,.ppt,.pptx"
                />
                {selectedFile && (
                  <p className="text-sm text-muted-foreground">
                    Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                  </p>
                )}
              </div>

              {/* Thumbnail */}
              <div className="grid gap-2">
                <Label htmlFor="thumbnail">Thumbnail (Optional)</Label>
                <Input
                  id="thumbnail"
                  type="file"
                  onChange={handleThumbnailSelect}
                  accept="image/*"
                />
              </div>

              {/* Title */}
              <div className="grid gap-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                  placeholder="e.g., Sunday Sermon - Faith and Works"
                />
              </div>

              {/* Description */}
              <div className="grid gap-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                  placeholder="Describe the content..."
                  rows={3}
                />
              </div>

              {/* Media Type and Category */}
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="media_type">Media Type *</Label>
                  <Select
                    value={uploadForm.media_type}
                    onValueChange={(value: any) => setUploadForm({ ...uploadForm, media_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="video">Video</SelectItem>
                      <SelectItem value="audio">Audio</SelectItem>
                      <SelectItem value="pdf">PDF</SelectItem>
                      <SelectItem value="document">Document</SelectItem>
                      <SelectItem value="image">Image</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="category">Category</Label>
                  <Select
                    value={uploadForm.category_id || 'none'}
                    onValueChange={(value) => setUploadForm({ ...uploadForm, category_id: value === 'none' ? '' : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Speaker and Date (for sermons) */}
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="speaker">Speaker</Label>
                  <Input
                    id="speaker"
                    value={uploadForm.speaker}
                    onChange={(e) => setUploadForm({ ...uploadForm, speaker: e.target.value })}
                    placeholder="e.g., Pastor John Doe"
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="sermon_date">Date</Label>
                  <Input
                    id="sermon_date"
                    type="date"
                    value={uploadForm.sermon_date}
                    onChange={(e) => setUploadForm({ ...uploadForm, sermon_date: e.target.value })}
                  />
                </div>
              </div>

              {/* Sermon Text */}
              <div className="grid gap-2">
                <Label htmlFor="sermon_text">Sermon Text (Bible Verse)</Label>
                <Input
                  id="sermon_text"
                  value={uploadForm.sermon_text}
                  onChange={(e) => setUploadForm({ ...uploadForm, sermon_text: e.target.value })}
                  placeholder="e.g., John 3:16 or Romans 8:28-39"
                />
              </div>

              {/* Tags */}
              <div className="grid gap-2">
                <Label htmlFor="tags">Tags (comma-separated)</Label>
                <Input
                  id="tags"
                  value={uploadForm.tags}
                  onChange={(e) => setUploadForm({ ...uploadForm, tags: e.target.value })}
                  placeholder="e.g., faith, grace, salvation"
                />
              </div>

              {/* Options */}
              <div className="flex gap-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="is_public"
                    checked={uploadForm.is_public}
                    onChange={(e) => setUploadForm({ ...uploadForm, is_public: e.target.checked })}
                    className="rounded"
                  />
                  <Label htmlFor="is_public" className="font-normal">Public (visible to all)</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="is_featured"
                    checked={uploadForm.is_featured}
                    onChange={(e) => setUploadForm({ ...uploadForm, is_featured: e.target.checked })}
                    className="rounded"
                  />
                  <Label htmlFor="is_featured" className="font-normal">Featured</Label>
                </div>
              </div>

              {/* Upload Progress */}
              {uploading && (
                <Alert>
                  <AlertDescription>
                    Uploading... {uploadProgress}%
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div
                        className="bg-primary h-2 rounded-full transition-all"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </AlertDescription>
                </Alert>
              )}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setUploadDialogOpen(false)}
                disabled={uploading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                disabled={!selectedFile || !uploadForm.title || uploading}
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Media</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total_items}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Views</CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total_views.toLocaleString()}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Videos</CardTitle>
            <Video className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.by_type['video'] || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Audio</CardTitle>
            <Music className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.by_type['audio'] || 0}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid gap-4 md:grid-cols-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search media..."
                className="pl-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger>
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger>
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="video">Video</SelectItem>
                <SelectItem value="audio">Audio</SelectItem>
                <SelectItem value="pdf">PDF</SelectItem>
                <SelectItem value="document">Document</SelectItem>
                <SelectItem value="image">Image</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger>
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="published">Published</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Media Grid */}
      {mediaItems.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Upload className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No media yet</h3>
            <p className="text-muted-foreground mb-4">Upload your first sermon or media file to get started</p>
            <Button onClick={() => setUploadDialogOpen(true)}>
              <Upload className="mr-2 h-4 w-4" />
              Upload Media
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {mediaItems.map((item) => (
            <Card key={item.id} className="overflow-hidden">
              {/* Thumbnail */}
              <div className="relative h-48 bg-muted">
                {item.thumbnail_url ? (
                  <img
                    src={item.thumbnail_url}
                    alt={item.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    {getMediaTypeIcon(item.media_type)}
                  </div>
                )}
                <div className="absolute top-2 right-2 flex gap-2">
                  {item.is_featured && (
                    <Badge variant="default">Featured</Badge>
                  )}
                  <Badge variant="secondary">{item.media_type}</Badge>
                </div>
              </div>

              <CardContent className="p-4">
                <h3 className="font-semibold mb-1 line-clamp-2">{item.title}</h3>
                {item.description && (
                  <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                    {item.description}
                  </p>
                )}

                {item.speaker && (
                  <p className="text-sm text-muted-foreground mb-1">
                    Speaker: {item.speaker}
                  </p>
                )}

                {item.sermon_text && (
                  <div className="mb-2">
                    <BibleVerseLink reference={item.sermon_text} variant="badge" />
                  </div>
                )}

                {item.category && (
                  <Badge variant="outline" className="mb-2">{item.category.name}</Badge>
                )}

                <div className="flex items-center justify-between text-sm text-muted-foreground mt-3 pt-3 border-t">
                  <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1">
                      <Eye className="h-3 w-3" />
                      {item.view_count}
                    </span>
                    {item.file_size && (
                      <span>{formatFileSize(item.file_size)}</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(item.file_url, '_blank')}
                    >
                      <Play className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(item.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
